/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable, OnDestroy } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { TooltipComponent } from './tooltip.component';

@Injectable({ providedIn: 'root' })
export class TooltipService implements OnDestroy {
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly environmentInjector: EnvironmentInjector = inject(EnvironmentInjector);

  private _componentRef: ComponentRef<TooltipComponent> | null = null;
  private _activeOwner: symbol | null = null;
  private _scrollSubscription: Subscription | null = null;

  get activeOwner(): symbol | null {
    return this._activeOwner;
  }

  get scrollSubscription(): Subscription | null {
    return this._scrollSubscription;
  }

  get tooltipComponentRef(): ComponentRef<TooltipComponent> {
    if (!this._componentRef) {
      this._componentRef = createComponent(TooltipComponent, { environmentInjector: this.environmentInjector });
      this.appRef.attachView(this._componentRef.hostView);
      document.body.appendChild(this._componentRef.location.nativeElement);
      this._scrollSubscription = fromEvent(globalThis, 'scroll', {
        capture: true,
        passive: true,
      } as AddEventListenerOptions).subscribe(() => this.forceHide());
    }
    return this._componentRef;
  }

  setActiveOwner(owner: symbol): void {
    this._activeOwner = owner;
  }

  mount(owner: symbol): void {
    if (this._activeOwner !== owner) return;
    this._showPopover();
  }

  show(owner: symbol): void {
    if (this._activeOwner !== owner) return;
    this.tooltipComponentRef.instance.show = true;
  }

  hide(owner: symbol): void {
    if (this._activeOwner !== owner) return;
    this.forceHide();
  }

  forceHide(): void {
    this._activeOwner = null;
    if (this._componentRef) {
      this._hidePopover();
      this._componentRef.instance.show = false;
    }
  }

  _showPopover(): void {
    const element: HTMLElement = this._getPopoverElement();
    if (!element?.matches(':popover-open')) element.showPopover();
  }

  _hidePopover(): void {
    const element: HTMLElement = this._getPopoverElement();
    if (element?.matches(':popover-open')) element.hidePopover();
  }

  _getPopoverElement(): HTMLElement {
    const host: HTMLElement = this._componentRef?.location.nativeElement;
    return (host?.children[0] as HTMLElement) ?? null;
  }

  ngOnDestroy(): void {
    this._scrollSubscription?.unsubscribe();
    this._componentRef?.destroy();
    this._componentRef = null;
  }
}
